import type { ConstValues } from '@openx/types/helpers.ts';
import { StatusTone } from '../../utils/statusTonePalette.ts';
import {
  asNonEmptyString,
  asOneOf,
  isUnvalidated,
  normalizeList,
  type ParsedOpenXAgentWidget,
} from '../../utils/widgetPayload.ts';

export const TargetingChipStatus = {
  INCLUDE: 'include',
  EXCLUDE: 'exclude',
} as const;

export type TargetingChipStatus = ConstValues<typeof TargetingChipStatus>;

export type TargetingChipItem = {
  id: string;
  label: string;
  value: string | null;
  status: TargetingChipStatus;
};

export type TargetingChipGroup = {
  id: string;
  label: string;
  items: TargetingChipItem[];
};

export type TargetingChipsPayload = {
  id: string;
  groups: TargetingChipGroup[];
};

// the Targeting Explainer mock (PR #9240) sends payloads without an id, so one is filled in
const DEFAULT_TARGETING_CHIPS_ID = 'targeting-chips';

export const getTargetingChipTone = (status: TargetingChipStatus): StatusTone =>
  status === TargetingChipStatus.INCLUDE ? StatusTone.POSITIVE : StatusTone.NEGATIVE;

const normalizeItem = (raw: unknown, fallbackId: string): TargetingChipItem | null => {
  if (!isUnvalidated<keyof TargetingChipItem>(raw)) return null;

  const label = asNonEmptyString(raw.label);
  const status = asOneOf(TargetingChipStatus, raw.status);
  if (label === null || status === null) return null;

  return { id: asNonEmptyString(raw.id) ?? fallbackId, label, value: asNonEmptyString(raw.value), status };
};

const normalizeGroup = (raw: unknown, fallbackId: string): TargetingChipGroup | null => {
  if (!isUnvalidated<keyof TargetingChipGroup>(raw)) return null;

  const id = asNonEmptyString(raw.id) ?? fallbackId;
  const label = asNonEmptyString(raw.label) ?? id;
  const items = normalizeList(raw.items, (item, index) => normalizeItem(item, `${id}-${index}`));

  return items.length > 0 ? { id, label, items } : null;
};

export const parseTargetingChipsPayload = (raw: unknown): ParsedOpenXAgentWidget<TargetingChipsPayload> => {
  if (!isUnvalidated<keyof TargetingChipsPayload>(raw)) {
    return { ok: false, error: 'Targeting chips payload is not an object.' };
  }

  const id = asNonEmptyString(raw.id) ?? DEFAULT_TARGETING_CHIPS_ID;

  const groups = normalizeList(raw.groups, (group, index) => normalizeGroup(group, `group-${index}`));
  if (groups.length === 0) return { ok: false, error: `Targeting chips widget "${id}" has no usable groups.` };

  return { ok: true, value: { id, groups } };
};
